import { isProgressEvent, type StreamingEvent } from '../../types/chat.types';
import { WORKFLOW_TOOL_NAME } from '../../types/constants';
import { splitEmoji } from '../../utils/emoji';
import { Logger } from '../../utils/logger';
import {
  type ContentProcessorState,
  type ContentNode,
  type ContentSegment,
  type ToolUseNode,
  MAX_NESTING_DEPTH,
} from '../contentProcessor.types';
import { appendText } from '../nodes';

/**
 * Converts content segments to a plain string for copying or display.
 * This is useful for components that need text-only representation.
 */
export function segmentsToString(segments: ContentSegment[]): string {
  return segments
    .map((segment) => (segment.type === 'text' ? segment.content : ''))
    .join('')
    .trim();
}

/**
 * Pre-processes content nodes into segments for efficient rendering.
 * This handles:
 * - Grouping consecutive tool-use nodes
 * - Marking the last text node for streaming cursor
 * - Calculating tool names for groups
 * - Validating node structure and recursion depth
 */
/**
 * Validates that content node recursion doesn't exceed maximum depth.
 * Prevents infinite recursion and stack overflow.
 */
function validateRecursionDepth(nodes: ContentNode[], depth: number): void {
  if (depth > MAX_NESTING_DEPTH) {
    throw new Error(
      `Content nesting depth exceeds maximum of ${MAX_NESTING_DEPTH}`,
    );
  }

  nodes.forEach((node) => {
    if (node.type === 'tool-use' && node.children?.length > 0) {
      validateRecursionDepth(node.children, depth + 1);
    }
  });
}

export function preprocessContentNodes(nodes: ContentNode[]): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let toolUseGroup: ToolUseNode[] = [];

  // Find the last text node with non-whitespace content
  let lastTextNodeIndex = -1;
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    if (node?.type === 'text' && node.content.trim().length > 0) {
      lastTextNodeIndex = i;
      break;
    }
  }

  const flushToolUseGroup = () => {
    if (toolUseGroup.length === 0) return;

    try {
      // Calculate unique tool names, stripping emojis
      const toolNames = Array.from(
        new Set(
          toolUseGroup.map((t) => {
            if (!t.data?.title) {
              Logger.warn({
                message: 'ToolUseNode missing title',
                data: { toolUseId: t.id },
              });
              return 'Unknown';
            }
            const [, titleWithoutEmoji] = splitEmoji(t.data.title);
            return titleWithoutEmoji || t.data.title;
          }),
        ),
      ).join(', ');

      const groupKey = `group-${toolUseGroup[0]?.id || 'unknown'}`;

      // Validate children recursion depth
      toolUseGroup.forEach((toolUse) => {
        validateRecursionDepth(toolUse.children, 1);
      });

      segments.push({
        type: 'tool-use-group',
        toolUses: toolUseGroup,
        toolNames,
        groupKey,
      });
    } catch (error) {
      Logger.error({
        message: 'Failed to create tool-use group',
        data: { error },
      });
      // Fallback: create individual groups for each tool use
      toolUseGroup.forEach((toolUse) => {
        segments.push({
          type: 'tool-use-group',
          toolUses: [toolUse],
          toolNames: toolUse.data?.title || 'Unknown',
          groupKey: toolUse.id || 'unknown',
        });
      });
    }

    toolUseGroup = [];
  };

  nodes.forEach((node, index) => {
    try {
      if (node == null || node.type == null) {
        Logger.warn({ message: 'Invalid content node', data: { index } });
        return;
      }

      if (node.type === 'text') {
        // Only flush the tool-use group if there's non-whitespace text
        // This matches the old behavior where consecutive tool-uses separated only by whitespace stay grouped
        if (node.content.trim().length > 0) {
          flushToolUseGroup();

          // Add text segment
          segments.push({
            type: 'text',
            content: node.content,
            isLastText: index === lastTextNodeIndex,
          });
        }
      } else if (node.type === 'image') {
        // Flush tool-use group before image
        flushToolUseGroup();

        // Add image segment
        segments.push({
          type: 'image',
          src: node.src,
          alt: node.alt,
          circuit_id: node.circuit_id,
          document_id: node.document_id,
          filename: node.filename,
        });
      } else if (node.type === 'artifact') {
        // Flush tool-use group before artifact
        flushToolUseGroup();

        // Add artifact segment
        segments.push({
          type: 'artifact',
          files: node.files,
          title: node.title,
        });
      } else if (node.type === 'a2ui-surface') {
        flushToolUseGroup();
        segments.push({
          type: 'a2ui-surface',
          surfaceId: node.surfaceId,
          toolUseId: node.toolUseId,
        });
      } else if (node.type === 'tool-use') {
        // Validate tool-use node structure
        if (node.data == null) {
          Logger.warn({ message: 'ToolUseNode missing data', data: { index } });
          return;
        }

        // Error tool uses should NOT be grouped - render them individually.
        // Workflow tool uses are likewise always standalone: they carry a
        // custom multi-agent progress render that must stay top-level and
        // visible (a collapsed "N tools" group would hide a live run).
        if (
          node.data.is_error === true ||
          node.data.name === WORKFLOW_TOOL_NAME
        ) {
          // Flush any existing group before rendering the standalone tool use
          flushToolUseGroup();

          // Render the tool use as its own segment (single item group)
          segments.push({
            type: 'tool-use-group',
            toolUses: [node],
            toolNames: node.data.title || 'Unknown',
            groupKey: node.id || 'unknown',
          });
        } else {
          // Add non-error tool uses to the current group
          toolUseGroup.push(node);
        }
      } else {
        Logger.warn({
          message: 'Unknown content node type',
          data: { index, type: (node as { type: string }).type },
        });
      }
    } catch (error) {
      Logger.error({
        message: 'Failed to process content node',
        data: { index, error },
      });
    }
  });

  // Flush any remaining tool-use group
  flushToolUseGroup();

  return segments;
}

/**
 * Adds a linebreak to the content when a progress event is followed by any other event type
 */
export function addPreviousEventFormatting(
  state: ContentProcessorState,
  event: StreamingEvent,
  previousEvent: StreamingEvent | undefined,
): ContentProcessorState {
  if (
    previousEvent &&
    !isProgressEvent(event) &&
    isProgressEvent(previousEvent)
  ) {
    return appendText(state, '\n\n');
  }
  return state;
}
